import { useCallback, useEffect, useRef, useState } from 'react';
import type { BannerCreative } from '../server/libs/siteSettings';
import type { ResolvedBannerInventory } from '../common/libs/bannerSettingsEx';
import { selectByWeight } from '../common/libs/bannerSettingsEx';

type UseBannerRotationResult = {
  currentCreative: BannerCreative | null;
};

/**
 * バナーのローテーションを管理するフック（クライアント側）。
 *
 * 仕様:
 * - 初回表示は ResolvedBannerInventory.initialCreativeId をそのまま使用する
 *   （SSRとhydrationの整合性、計測の安定性のため）
 * - 2回目以降のみ selectByWeight で再抽選する
 * - rotationIntervalSec > 0 かつ inventories が2件以上の場合、指定秒数ごとに再抽選
 * - rotationIntervalSec <= 0 または inventories が1件の場合は固定表示
 * - resolvedInventory が null/undefined の場合は currentCreative=null を返す
 * - 再抽選時は直前に表示していたクリエイティブを除外し、連続同一表示を回避する
 *   （候補が1件のみの場合は除外せず通常抽選にフォールバック）
 */
export function useBannerRotation(
  resolvedInventory: ResolvedBannerInventory | null | undefined,
): UseBannerRotationResult {
  /**
   * 現在表示中のクリエイティブIDを ref で保持し、
   * setInterval クロージャから常に最新値を参照できるようにする。
   */
  const currentCreativeIdRef = useRef<string | null>(
    resolvedInventory?.initialCreativeId ?? null,
  );

  /**
   * excludeId を除外したinventoryのリストから重み付き抽選する。
   * 候補が0件になる場合はnullを返す
   */
  const pickWeightedCreativeId = useCallback(
    (excludeId?: string | null): string | null => {
      if (!resolvedInventory || resolvedInventory.inventories.length === 0)
        return null;

      // 直前表示の creativeId を除外（候補が1件以下なら除外しない）
      const candidates =
        excludeId && resolvedInventory.inventories.length > 1
          ? resolvedInventory.inventories.filter(
              (e) => e.creativeId !== excludeId,
            )
          : resolvedInventory.inventories;

      // 想定外データ（creativeId重複など）で空になった場合のガード
      if (candidates.length === 0) return null;

      return selectByWeight(candidates);
    },
    [resolvedInventory],
  );

  // 初回はサーバー決定値を表示する
  const [currentCreative, setCurrentCreative] = useState<BannerCreative | null>(
    () => {
      if (!resolvedInventory) return null;
      return (
        resolvedInventory.creatives[resolvedInventory.initialCreativeId] ?? null
      );
    },
  );

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // resolvedInventory が変わったら、改めて初回表示をサーバー決定値に同期する
    const initial = resolvedInventory
      ? resolvedInventory.creatives[resolvedInventory.initialCreativeId] ?? null
      : null;
    setCurrentCreative(initial);
    currentCreativeIdRef.current = resolvedInventory?.initialCreativeId ?? null;

    const intervalSec =
      resolvedInventory?.displayOptions.rotationIntervalSec ?? 0;
    const hasMultipleInventories =
      (resolvedInventory?.inventories.length ?? 0) > 1;

    if (resolvedInventory && intervalSec > 0 && hasMultipleInventories) {
      intervalRef.current = setInterval(() => {
        // ref 経由で現在表示中のIDを取得し、連続抽選を回避する
        const nextCreativeId = pickWeightedCreativeId(
          currentCreativeIdRef.current,
        );
        currentCreativeIdRef.current = nextCreativeId;
        setCurrentCreative(
          nextCreativeId
            ? resolvedInventory.creatives[nextCreativeId] ?? null
            : null,
        );
      }, intervalSec * 1000);
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [resolvedInventory, pickWeightedCreativeId]);

  return { currentCreative };
}
